'use client';

import { useMemo, useState } from 'react';
import { TZDate } from '@date-fns/tz';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';

import { MultiSelect } from '@/components/atoms/multi-select';
import { Select } from '@/components/atoms/select';
import { useActiveEmployees } from '@/components/providers/employee-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEditableState } from '@/hooks/use-editable-state';

interface RouteFormData {
  description: string;
  start_at: string;
  end_at: string;
  employee_id: string | null;
  region_ids: string[];
  issue_ids: string[];
  skill_ids: string[];
}

export function CreateRouteDialog() {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const { mutate, isPending } = api.routes.createRoutesPost.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Route created');
        api.routes.searchRoutesSearchGet.invalidateQueries();
        setOpen(false);
      },
    }
  );

  const [formValues, setFormValues] = useState<RouteFormData>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const startAt = new Date(tomorrow);
    startAt.setHours(9, 0, 0, 0);

    const endAt = new Date(tomorrow);
    endAt.setHours(17, 0, 0, 0);

    return {
      description: '',
      start_at: startAt.toISOString().slice(0, 16),
      end_at: endAt.toISOString().slice(0, 16),
      employee_id: null,
      region_ids: [],
      issue_ids: [],
      skill_ids: [],
    };
  });

  const isDisabled = useMemo(() => {
    if (!formValues.start_at || !formValues.end_at) return true;
    if (isPending) return true;
    return false;
  }, [formValues.start_at, formValues.end_at, isPending]);

  const updateFormValues = (updates: Partial<RouteFormData>) => {
    setFormValues((prev) => ({ ...prev, ...updates }));
  };

  const onSubmit = () => {
    if (!formValues.start_at || !formValues.end_at) {
      toast.error('Start and end times are required');
      return;
    }
    if (new Date(formValues.start_at) >= new Date(formValues.end_at)) {
      toast.error('Start time must be before end time');
      return;
    }
    mutate({
      office_id: officeId,
      description: formValues.description,
      start_at: formValues.start_at,
      end_at: formValues.end_at,
      employee_id: formValues.employee_id,
      region_ids: formValues.region_ids,
      issue_ids: formValues.issue_ids,
      skill_ids: formValues.skill_ids,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size='sm'>
          <PlusIcon className='size-4' />
          Add Route
        </Button>
      </DialogTrigger>
      <DialogContent showClose>
        <DialogHeader>
          <DialogTitle>Add Route</DialogTitle>
          <DialogDescription>Add a route and specify its scheduling details.</DialogDescription>
        </DialogHeader>
        <RouteForm values={formValues} setValues={updateFormValues} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={isDisabled}>
            {isPending ? 'Creating...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditRouteDialogProps {
  route: ApiSchemas['RouteRead'];
  trigger: React.ReactNode;
}

export function EditRouteDialog({ route, trigger }: EditRouteDialogProps) {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { office } = useOfficeCtx();

  const { value, setValue, isDirty, reset } = useEditableState(route, (r) => ({
    description: r.description ?? '',
    start_at: new TZDate(r.start_at, office.timezone ?? 'UTC').toISOString().slice(0, 16),
    end_at: new TZDate(r.end_at, office.timezone ?? 'UTC').toISOString().slice(0, 16),
    employee_id: r.employee_id,
    region_ids: r.regions.map((region) => region.region_id),
    issue_ids: r.issues.map((issue) => issue.issue_id),
    skill_ids: r.skills.map((skill) => skill.skill_id),
  }));

  const { mutate, isPending } = api.routes.updateRoutesRouteIdPatch.useMutation(
    {
      path: {
        route_id: route.id,
      },
    },
    {
      onSuccess: (data) => {
        toast.success('Route updated');
        reset(data);
        api.routes.searchRoutesSearchGet.invalidateQueries();
        setOpen(false);
      },
    }
  );

  const isDisabled = useMemo(() => {
    if (!value.start_at || !value.end_at) return true;
    if (isPending) return true;
    if (!isDirty) return true;
    return false;
  }, [value.start_at, value.end_at, isPending, isDirty]);

  const onSubmit = () => {
    if (!value.start_at || !value.end_at) {
      toast.error('Start and end times are required');
      return;
    }
    if (new Date(value.start_at) >= new Date(value.end_at)) {
      toast.error('Start time must be before end time');
      return;
    }
    mutate({
      description: value.description,
      start_at: value.start_at, // TODO: send UTC
      end_at: value.end_at,
      employee_id: value.employee_id,
      region_ids: value.region_ids,
      issue_ids: value.issue_ids,
      skill_ids: value.skill_ids,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent showClose>
        <DialogHeader>
          <DialogTitle>Edit Route</DialogTitle>
          <DialogDescription>Update the route and specify its scheduling details.</DialogDescription>
        </DialogHeader>
        <RouteForm values={value} setValues={(updates) => setValue((prev) => ({ ...prev, ...updates }))} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={true}>
            {isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RouteFormProps {
  values: RouteFormData;
  setValues: (values: Partial<RouteFormData>) => void;
}

export function RouteForm({ values, setValues }: RouteFormProps) {
  const { office } = useOfficeCtx();
  const activeEmployees = useActiveEmployees({ sortBy: 'first_name' });
  const options = useMemo(() => {
    return {
      employees: activeEmployees.map((employee) => ({
        value: employee.id,
        label: `${employee.first_name} ${employee.last_name}`,
      })),
      regions: office.regions.map((region) => ({
        value: region.id,
        label: region.name,
      })),
      issues: office.issues.map((issue) => ({
        value: issue.id,
        label: issue.name,
      })),
      skills: office.skills.map((skill) => ({
        value: skill.id,
        label: skill.name,
      })),
    };
  }, [office, activeEmployees]);

  return (
    <div className='grid gap-4 py-4'>
      <div className='grid grid-cols-4 items-center gap-2'>
        <Label htmlFor='description'>Description</Label>
        <Input
          id='description'
          value={values.description}
          onChange={(e) => setValues({ description: e.target.value })}
          className='col-span-3'
        />
      </div>
      <div className='grid grid-cols-4 items-center gap-2'>
        <Label htmlFor='start_at'>Start at</Label>
        <Input
          id='start_at'
          type='datetime-local'
          value={values.start_at}
          onChange={(e) => setValues({ start_at: e.target.value })}
          className='col-span-3'
        />
      </div>
      <div className='grid grid-cols-4 items-center gap-2'>
        <Label htmlFor='end_at'>End at</Label>
        <Input
          id='end_at'
          type='datetime-local'
          value={values.end_at}
          onChange={(e) => setValues({ end_at: e.target.value })}
          className='col-span-3'
          required
        />
      </div>
      <div className='grid grid-cols-4 items-center gap-2'>
        <Label htmlFor='employee_id'>Employee</Label>
        <Select
          options={options.employees}
          value={values.employee_id ?? ''}
          onChange={(value) => setValues({ employee_id: value })}
          className='col-span-3 w-full'
        />
      </div>
      <div className='grid grid-cols-4 items-center gap-2'>
        <Label htmlFor='region_ids'>Regions</Label>
        <MultiSelect
          label='Regions'
          options={options.regions}
          selected={values.region_ids}
          onChange={(value) => setValues({ region_ids: value })}
          className='col-span-3 w-full justify-start'
        />
      </div>
      <div className='grid grid-cols-4 items-center gap-2'>
        <Label htmlFor='issue_ids'>Issues</Label>
        <MultiSelect
          label='Issues'
          options={options.issues}
          selected={values.issue_ids}
          onChange={(value) => setValues({ issue_ids: value })}
          className='col-span-3 w-full justify-start'
        />
      </div>
      <div className='grid grid-cols-4 items-center gap-2'>
        <Label htmlFor='skill_ids'>Skills</Label>
        <MultiSelect
          label='Skills'
          options={options.skills}
          selected={values.skill_ids}
          onChange={(value) => setValues({ skill_ids: value })}
          className='col-span-3 w-full justify-start'
        />
      </div>
    </div>
  );
}
